import { useState } from "react";
import { FollowUpLetterDto } from "../../types/Rows";
import { Button } from "../../components/ui/button";
import { field } from "../../components/ui/styles";

type ApplyField = {
  key: keyof FollowUpLetterDto;
  label: string;
  value: string;
};

const getRadioText = (val?: string) => {
  if (val === "yes") return "TAK";
  if (val === "no") return "NIE";
  if (val === "na") return "NA";
  return val ?? "";
};

const buildStudyProgress = (answers: FollowUpLetterDto["answers"]): string =>
  answers
    .map((a, i) => {
      const result = getRadioText(a.answer);
      const comment = a.comment ? ` – ${a.comment}` : "";
      return `${i + 1}. ${a.description}: ${result}${comment}`;
    })
    .join("\n");

export function ReportDataPanel({
  data,
  onApply,
}: {
  data: FollowUpLetterDto;
  onApply: (fields: Partial<FollowUpLetterDto>) => void;
}) {
  const [open, setOpen] = useState(false);

  const candidates: ApplyField[] = [
    { key: "sponsor",            label: "Sponsor",            value: data.sponsor },
    { key: "visitType",          label: "Typ wizyty",         value: data.visitType },
    { key: "croParticipants",    label: "Uczestnicy CRO",     value: data.croParticipants },
    { key: "resortParticipants", label: "Uczestnicy ośrodka", value: data.resortParticipants },
  ];

  if (data.answers?.length > 0) {
    candidates.push({
      key: "studyProgress",
      label: "Postęp badania (z odpowiedzi raportu)",
      value: buildStudyProgress(data.answers),
    });
  }

  const availableFields = candidates.filter((f) => f.value && f.value.trim() !== "");

  const [selected, setSelected] = useState<Set<keyof FollowUpLetterDto>>(
    () => new Set(availableFields.map((f) => f.key))
  );

  const toggleField = (key: keyof FollowUpLetterDto) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleApply = (keys: Set<keyof FollowUpLetterDto>) => {
    const patch: Partial<FollowUpLetterDto> = {};
    availableFields.forEach((f) => {
      if (keys.has(f.key)) (patch as any)[f.key] = f.value;
    });
    onApply(patch);
    setOpen(false);
  };

  if (availableFields.length === 0) return null;

  const labelCls = "block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em]";

  return (
    <div className="rounded-lg border border-[#C5D8F5] bg-[#EEF4FF] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-[12px] font-semibold text-[#3570C4] hover:bg-[#DDEAFF] transition-colors"
      >
        <span>Dane z raportu</span>
        <span className="text-[10px] font-normal opacity-70">
          {open ? "▲ zwiń" : "▼ rozwiń"}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2 border-t border-[#C5D8F5]">
          <p className="text-[11px] text-[#3570C4] opacity-80">
            Zaznacz pola które chcesz wciągnąć do formularza:
          </p>

          <div className="space-y-1">
            {availableFields.map((f) => (
              <label
                key={f.key}
                className="flex items-start gap-2.5 px-2 py-1.5 rounded-[6px] hover:bg-[#DDEAFF] cursor-pointer"
              >
                <input
                  type="checkbox"
                  className={field.checkbox}
                  checked={selected.has(f.key)}
                  onChange={() => toggleField(f.key)}
                />
                <div className="min-w-0">
                  <div className={`${labelCls} normal-case tracking-normal font-semibold text-[#3570C4]`}>
                    {f.label}
                  </div>
                  <div className="text-[11px] text-[var(--sc-muted)] truncate max-w-[320px]">
                    {f.value.length > 80 ? f.value.slice(0, 80) + "…" : f.value}
                  </div>
                </div>
              </label>
            ))}
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              onClick={() => handleApply(selected)}
              disabled={selected.size === 0}
            >
              Uzupełnij zaznaczone
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => handleApply(new Set(availableFields.map((f) => f.key)))}
            >
              Uzupełnij wszystko
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
